import { useOne } from '@refinedev/core';
import { useQuery } from '@tanstack/react-query';
import { Form, SelectProps } from 'antd';
import { FormProps } from 'antd/lib';
import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';

import useFormInitialSet from '@/hooks/useFormInitialSet';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import CompanyContact from '@/model/General/CompanyContact';
import { Ticket } from '@/model/Ticket/Ticket';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

export const useReceptionForm = (formProps: FormProps, initialValuesModified, edit?: boolean) => {
  const { form } = formProps;
  const { initialValuesSetted } = useFormInitialSet(
    form,
    initialValuesModified,
    [
      { watchField: 'company', resetFields: ['company_contact'] },
      { watchField: 'ticket', resetFields: ['company_contact'] },
    ],
    {
      withoutInitial: !edit && !initialValuesModified?.ticket,
    },
  );
  const { companySelectProps, shippersSelectProps, ticketsSelectProps } = useOutletContext<{
    companySelectProps: SelectProps;
    shippersSelectProps: SelectProps;
    ticketsSelectProps: SelectProps;
  }>();
  const companySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: companySelectProps,
    resource: 'empresas',
    initialValue: initialValuesModified?.company,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const shippersSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: shippersSelectProps,
    resource: RESOURCES.SHIPPERS,
    initialValue: initialValuesModified?.shipper,
    labelKey: 'name',
    valueKey: 'id',
  });
  const ticketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: ticketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: initialValuesModified?.ticket,
    labelKey: (data: any) =>
      `${data.id} - ${data.title ? data.title : data.ticket_title_str ? data.ticket_title_str : '-'}`,
    valueKey: 'id',
  });
  const [movilLink, setMovilLink] = useState<string>('');
  /**
   * Seteo automatico de empresa
   */
  const [selectedTicketOption, setSelectedTicketOption] = useState<Ticket>();

  useEffect(() => {
    if (initialValuesModified?.ticket) {
      if (ticketsSelectPropsWithInitials.options.length > 0) {
        const index = ticketsSelectPropsWithInitials.options.findIndex(
          option => option.value === initialValuesModified?.ticket,
        );

        if (index !== -1) {
          setSelectedTicketOption(ticketsSelectPropsWithInitials.options[index].item);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesModified?.ticket]);

  // busqueda de la empresa del ticket
  const { result } = useOne({
    resource: 'empresas',
    id: selectedTicketOption?.empresa,
    queryOptions: {
      enabled: !!selectedTicketOption?.empresa,
    },
  });
  // Se arma la opcion de empresa del ticket
  const ticketCompanyOption = useMemo(
    () =>
      result
        ? [
            {
              label: result.full_name,
              value: result.id,
              cuit: result.cuit,
            },
          ]
        : [],
    [result],
  );

  useEffect(() => {
    if (selectedTicketOption && ticketCompanyOption.length > 0 && !edit) {
      form?.setFieldValue('company', ticketCompanyOption[0].value);

      setMovilLink(selectedTicketOption?.movil_link || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketOption, ticketCompanyOption]);

  const ticketsSelectPropsModified = {
    ...ticketsSelectPropsWithInitials,
    onChange: (v, option: any) => {
      ticketsSelectProps?.onChange?.(v, option);
      setSelectedTicketOption(option?.item as Ticket);
    },
  };
  const companySelectPropsModified = useMemo(
    () => ({
      ...companySelectPropsWithInitials,
      options: [
        ...ticketCompanyOption,
        ...(companySelectPropsWithInitials.options?.filter(
          c => c.value != ticketCompanyOption[0]?.value,
        ) || []),
      ],
    }),
    [companySelectPropsWithInitials, ticketCompanyOption],
  );
  // Contactos para envio de presupuesto
  const company = Form.useWatch('company', form);
  const { companyRepository } = useRepositoriesContext();
  const ticketCompanyId = ticketCompanyOption[0]?.value as number | undefined;
  const selectedCompanyId = company ?? (!edit ? ticketCompanyId : undefined);
  const shouldFetchSelectedCompany = typeof selectedCompanyId === 'number';
  const shouldFetchTicketCompany =
    typeof ticketCompanyId === 'number' && ticketCompanyId !== selectedCompanyId;
  const { data: selectedCompanyContacts, isFetching: loadingSelectedCompanyContacts } = useQuery({
    queryKey: ['companyContacts', selectedCompanyId],
    queryFn: () => companyRepository.getCompanyContact(selectedCompanyId as number),
    enabled: shouldFetchSelectedCompany,
  });
  const { data: ticketCompanyContacts, isFetching: loadingTicketCompanyContacts } = useQuery({
    queryKey: ['companyContacts', ticketCompanyId],
    queryFn: () => companyRepository.getCompanyContact(ticketCompanyId!),
    enabled: shouldFetchTicketCompany,
  });
  const companyContactData = useMemo<{
    selectedCompany?: CompanyContact[];
    ticketCompany?: CompanyContact[];
  }>(
    () => ({
      selectedCompany: shouldFetchSelectedCompany ? (selectedCompanyContacts ?? []) : undefined,
      ticketCompany: shouldFetchTicketCompany ? (ticketCompanyContacts ?? []) : undefined,
    }),
    [
      selectedCompanyContacts,
      shouldFetchSelectedCompany,
      ticketCompanyContacts,
      shouldFetchTicketCompany,
    ],
  );
  const loadingCompanyContactData = loadingSelectedCompanyContacts || loadingTicketCompanyContacts;
  const [sendToClient, setSendToClient] = useState(
    formProps.initialValues?.company_contact ? true : false,
  );

  return {
    initialValuesSetted,
    companySelectProps: companySelectPropsModified,
    shippersSelectProps: shippersSelectPropsWithInitials,
    ticketsSelectProps: ticketsSelectPropsModified,
    // Contactos para envio de presupuesto
    companyContactData,
    loadingCompanyContactData,
    sendToClient,
    setSendToClient,
    // link al movil
    movilLink,
  };
};
